import { useEffect, useState } from "react";
import logger from "../utils/logger";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Key, Envelope, QrCode } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

function TOTPSetupForm({ onSetupComplete }) {
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);

  const setupTOTP = async () => {
    try {
      const res = await axios.post(`${API}/admin/2fa/setup-totp`, {}, { headers: getAuthHeaders() });
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
    } catch { toast.error("Failed to setup TOTP"); }
  };

  const verifyTOTP = async () => {
    if (!code || code.length !== 6) { toast.error("Enter a 6-digit code"); return; }
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/2fa/verify-totp`, { code }, { headers: getAuthHeaders() });
      toast.success("TOTP 2FA enabled!");
      onSetupComplete();
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    finally { setVerifying(false); }
  };

  if (!qrCode) {
    return (
      <button data-testid="setup-totp-btn" onClick={setupTOTP} className="px-4 py-2 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors flex items-center gap-1.5">
        <QrCode size={14} /> Generate QR Code
      </button>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-center p-4 rounded-md bg-white">
        <img src={qrCode} alt="QR Code" className="w-40 h-40" data-testid="totp-qr-code" />
      </div>
      <p className="text-[10px] text-[#A1A1AA] text-center">
        Or enter manually: <code className="text-[#00E5FF] bg-white/5 px-1 rounded text-[10px]">{secret}</code>
      </p>
      <div className="flex gap-2 justify-center">
        <Input data-testid="totp-verify-input" placeholder="Enter 6-digit code" value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6}
          className="bg-transparent border-white/10 text-white text-center text-lg tracking-[8px] font-mono w-48 focus-visible:ring-[#00E5FF]" />
        <button data-testid="verify-totp-btn" onClick={verifyTOTP} disabled={verifying}
          className="px-4 py-2 rounded-md text-xs font-bold text-black bg-[#00E5FF] hover:bg-[#00B8CC] transition-colors disabled:opacity-50">
          {verifying ? "Verifying..." : "Verify & Enable"}
        </button>
      </div>
    </div>
  );
}

function EmailOTPForm({ userEmail, onSetupComplete }) {
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  const sendEmailOTP = async () => {
    setSendingOtp(true);
    try {
      const res = await axios.post(`${API}/admin/2fa/send-email-otp`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
    } catch { toast.error("Failed to send OTP"); }
    finally { setSendingOtp(false); }
  };

  const verifyEmailOTP = async () => {
    if (!code || code.length !== 6) { toast.error("Enter a 6-digit code"); return; }
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/2fa/verify-email-otp`, { code }, { headers: getAuthHeaders() });
      toast.success("Email OTP 2FA enabled!");
      onSetupComplete();
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    finally { setVerifying(false); }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <button data-testid="send-email-otp-btn" onClick={sendEmailOTP} disabled={sendingOtp}
        className="px-4 py-2 rounded-md text-xs font-bold border border-white/20 text-white hover:border-white/40 transition-colors disabled:opacity-50">
        {sendingOtp ? "Sending..." : `Send OTP to ${userEmail || "email"}`}
      </button>
      <Input data-testid="email-otp-input" placeholder="6-digit code" value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6}
        className="bg-transparent border-white/10 text-white text-center text-lg tracking-[8px] font-mono w-48 focus-visible:ring-[#00E5FF]" />
      <button data-testid="verify-email-otp-btn" onClick={verifyEmailOTP} disabled={verifying}
        className="px-4 py-2 rounded-md text-xs font-bold text-black bg-[#F59E0B] hover:bg-[#D97706] transition-colors disabled:opacity-50">
        {verifying ? "Verifying..." : "Verify & Enable"}
      </button>
    </div>
  );
}

function DisableTwoFA({ onDisabled }) {
  const [disableCode, setDisableCode] = useState("");

  const disable2FA = async () => {
    if (!disableCode) { toast.error("Enter your current 2FA code to disable"); return; }
    try {
      await axios.post(`${API}/admin/2fa/disable`, { code: disableCode }, { headers: getAuthHeaders() });
      toast.success("2FA disabled");
      onDisabled();
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
  };

  return (
    <div className="p-4 rounded-md border border-white/10 bg-white/[0.02]">
      <p className="text-xs text-[#A1A1AA] mb-3">Enter your current 2FA code to disable:</p>
      <div className="flex gap-2">
        <Input data-testid="disable-2fa-code" placeholder="6-digit code" value={disableCode}
          onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6}
          className="bg-transparent border-white/10 text-white text-center text-lg tracking-[8px] font-mono w-48 focus-visible:ring-[#00E5FF]" />
        <button data-testid="disable-2fa-btn" onClick={disable2FA}
          className="px-4 py-2 rounded-md text-xs font-bold text-[#EF4444] border border-[#EF4444]/20 bg-[#EF4444]/10 hover:bg-[#EF4444]/20 transition-colors">
          Disable 2FA
        </button>
      </div>
    </div>
  );
}

export default function TwoFASetup() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = () => {
    setLoading(true);
    axios.get(`${API}/admin/2fa/status`, { headers: getAuthHeaders() })
      .then((res) => setStatus(res.data))
      .catch((err) => logger.error("2FA status error:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatus(); }, []);

  if (loading) return <div className="h-20 flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="two-fa-setup">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(0, 229, 255, 0.1)" }}>
          <ShieldCheck size={20} weight="duotone" className="text-[#00E5FF]" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Two-Factor Authentication</h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">
            {status?.enabled ? (
              <Badge variant="outline" className="text-[10px] border bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20">
                Enabled ({status.method === "totp" ? "Authenticator App" : "Email OTP"})
              </Badge>
            ) : "Add an extra layer of security to your account"}
          </p>
        </div>
      </div>

      {status?.enabled ? (
        <DisableTwoFA onDisabled={fetchStatus} />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-md border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-3">
              <Key size={16} className="text-[#00E5FF]" />
              <span className="text-xs font-bold text-white">Option 1: Authenticator App (TOTP)</span>
            </div>
            <TOTPSetupForm onSetupComplete={fetchStatus} />
          </div>
          <div className="p-4 rounded-md border border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2 mb-3">
              <Envelope size={16} className="text-[#F59E0B]" />
              <span className="text-xs font-bold text-white">Option 2: Email OTP</span>
              <span className="text-[10px] text-[#A1A1AA]">(Requires Resend email configured)</span>
            </div>
            <EmailOTPForm userEmail={user?.email} onSetupComplete={fetchStatus} />
          </div>
        </div>
      )}
    </div>
  );
}
